import { AbstractControl, FormArray, FormGroup, ValidationErrors } from '@angular/forms';
export function isTrue(input: null | boolean): boolean {
  return input !== null && !!input;
}

export function isNotNull<T>(input: null | T): input is T {
  return input !== null;
}

export function isNotNullOrUndefined<T>(input: null | undefined | T): input is T {
  return input !== null && input !== undefined;
}

export function webglSupport(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
    );
  } catch (_e) {
    return false;
  }
}

type NonFunctional<T> = T extends (...args: unknown[]) => unknown ? never : T;

/**
 * Converts the given enum to a map of the keys to the values.
 * @param enumeration The enum to convert to a map.
 */
export function enumToMap<T extends Record<string, unknown>>(
  enumeration: T
): Map<string, NonFunctional<T[keyof T]>> {
  const map = new Map<string, NonFunctional<T[keyof T]>>();
  for (const key in enumeration) {
    //TypeScript does not allow enum keys to be numeric
    if (!isNaN(Number(key))) continue;

    const val = enumeration[key] as NonFunctional<T[keyof T]>;

    //TypeScript does not allow enum value to be null or undefined
    if (val !== undefined && val !== null) map.set(key, val);
  }

  return map;
}

export function enumToArray<T extends Record<string, unknown>>(
  enumeration: T
): Array<NonFunctional<T[keyof T]>> {
  return Array.from(enumToMap(enumeration).values());
}

export function validateForm(form: FormGroup): boolean {
  if (form.valid) {
    return true;
  } else {
    const rawFields = Object.keys(form.controls).filter((k) => form.controls[k].invalid);

    const controls = Object.values(form.controls).filter((c) => c.invalid);
    console.warn('Invalid form fields: ', rawFields.join(', '));
    console.warn(
      'errored control values',
      controls.map((c) => c.value as string)
    );
    console.warn(
      'Errors: ',
      controls.map((c) => c.errors)
    );
    return false;
  }
}

export interface InvalidField {
  field: string;
  errors: ValidationErrors;
}

function collectInvalidFields(control: AbstractControl, path: string): InvalidField[] {
  const result: InvalidField[] = [];

  if (control instanceof FormGroup) {
    for (const key of Object.keys(control.controls)) {
      const child = control.controls[key];
      const childPath = path ? `${path}.${key}` : key;
      result.push(...collectInvalidFields(child, childPath));
    }
  } else if (control instanceof FormArray) {
    control.controls.forEach((child, index) => {
      const childPath = `${path}[${index}]`;
      result.push(...collectInvalidFields(child, childPath));
    });
  } else if (control.invalid && control.errors) {
    result.push({ field: path, errors: control.errors });
  }

  return result;
}

export function getInvalidFields(form: FormGroup): InvalidField[] {
  if (form.valid) {
    return [];
  }

  const invalidFields = collectInvalidFields(form, '');

  console.warn('Invalid form fields: ', invalidFields.map((f) => f.field).join(', '));
  console.warn(
    'Errors: ',
    invalidFields.map((f) => ({ field: f.field, errors: f.errors }))
  );

  return invalidFields;
}

export const calculatePercentage = (max: number, current: number): number => {
  return Number(((current * 100) / max).toFixed(2));
};

export const parseDateToISOS = (date: Date): string => {
  date.setHours(10, 0, 0);
  return date.toISOString().split('T')[0];
};

export const parseISOSToDate = (date: string): Date => {
  if (/^\d{2}-\d{4}$/.test(date)) {
    date = `01-${date}`;
  }
  const [day, month, year] = date.split('-').map(Number);

  return new Date(year, month - 1, day);
};

export const formatNumber = (n: number): string => {
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const capitalizeString = (s: string): string => {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

export const normalizeString = (string: string): string => {
  return string
    .toLocaleLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  // return string.normalize('NFD').replace(/\p{Diacritic}/gu, '');
};

export function initials(name: string): string {
  const words = name.trim().split(' ');
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  } else {
    return words[0].charAt(0).toUpperCase() + words[words.length - 1].charAt(0).toUpperCase();
  }
}

export function compareNormalizedStrings(a: string, b: string): boolean {
  return normalizeString(a) === normalizeString(b);
}
